import React, { useState } from "react";
import { AlertCircle, CalendarOff, Loader2, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { BookingModuleGuard, BookingModuleHeader } from "../components/booking-module-shell";
import {
  createBlocked,
  deleteBlocked,
  fetchBlocked,
  fetchBookingSettings,
  formatInstant,
} from "../lib/booking";
import type { ClientOutletContext } from "../lib/outlet-context";

function BlockedTimes({ api }: ClientOutletContext) {
  const settingsQuery = useApiQuery(() => (api ? fetchBookingSettings(api) : null), [api]);
  const timezone = settingsQuery.data?.settings.timezone ?? "Europe/London";

  const query = useApiQuery(() => (api ? fetchBlocked(api) : null), [api]);
  const blocked = query.data?.blocked ?? null;

  const [addOpen, setAddOpen] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState<string | null>(null);

  const openAdd = () => {
    setStart("");
    setEnd("");
    setReason("");
    setFormError("");
    setAddOpen(true);
  };

  const add = async () => {
    if (!api) return;
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
      setFormError("Enter a start and a later end.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await createBlocked(api, {
        starts_at: new Date(startMs).toISOString(),
        ends_at: new Date(endMs).toISOString(),
        reason: reason.trim() || null,
      });
      setAddOpen(false);
      query.reload();
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!api) return;
    setRemovingId(id);
    setError("");
    try {
      await deleteBlocked(api, id);
      query.reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div>
      <BookingModuleHeader
        action={
          <Button size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4" />
            Block time
          </Button>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      {!blocked && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {blocked && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">From</th>
                  <th className="px-4 py-3 font-medium">Until</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {blocked.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                      Nothing blocked — add holidays or one-off closures here.
                    </td>
                  </tr>
                )}
                {blocked.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">{formatInstant(entry.starts_at, timezone)}</td>
                    <td className="px-4 py-3">{formatInstant(entry.ends_at, timezone)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {entry.reason || <span className="flex items-center gap-1.5"><CalendarOff className="h-3.5 w-3.5" />—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <Button
                          variant="ghost" size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          title="Remove blocked time"
                          disabled={removingId === entry.id}
                          onClick={() => { void remove(entry.id); }}
                        >
                          {removingId === entry.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block time off</DialogTitle>
            <DialogDescription>
              No slots are offered while time is blocked — use it for holidays, private
              events, or maintenance. Times are entered in your device's timezone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="blk-start">From</Label>
                <Input id="blk-start" type="datetime-local" value={start}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStart(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="blk-end">Until</Label>
                <Input id="blk-end" type="datetime-local" value={end}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEnd(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blk-reason">Reason (optional)</Label>
              <Input id="blk-reason" value={reason} placeholder="e.g. Summer holiday"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)} />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={() => { void add(); }} disabled={saving || !start || !end}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Block time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BookingBlockedPage() {
  return (
    <BookingModuleGuard title="Bookings" description="Holidays and one-off closures.">
      {(ctx) => <BlockedTimes {...ctx} />}
    </BookingModuleGuard>
  );
}
